import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

type SearchParamsSetter = ReturnType<typeof useSearchParams>[1];
type NextInit = Parameters<SearchParamsSetter>[0];
type NavigateOpts = Parameters<SearchParamsSetter>[1];

/**
 * Drop-in replacement for react-router's `useSearchParams` that applies
 * *functional* updates against the LIVE URL (`window.location.search`) instead
 * of the React snapshot captured when the setter was created.
 *
 * Why: react-router's `setSearchParams(prev => …)` runs `prev` against a
 * snapshot of the params from the render that produced the setter, then does a
 * full `navigate("?" + result)`. A stale setter — e.g. a delayed async
 * callback (an auto-correct effect firing after a slow backend response) or a
 * component that hasn't re-rendered yet — rebuilds the *entire* query string
 * from an out-of-date snapshot and silently drops params added since. In this
 * app that manifested as the anchor flipping BESTÅ→PAX when changing a page or
 * dropdown, intermittently and only on the (slow, scale-to-zero) dev backend
 * where the race window is wide.
 *
 * Reading the live URL at mutation time makes updates immune to that race.
 * Reads (the returned params) are unchanged — they still reflect the committed
 * URL via normal re-render.
 */
export function useLiveSearchParams(): [URLSearchParams, SearchParamsSetter] {
    const [searchParams, setSearchParams] = useSearchParams();

    const liveSetSearchParams = useCallback<SearchParamsSetter>(
        (nextInit?: NextInit, navigateOpts?: NavigateOpts) => {
            if (typeof nextInit === "function") {
                const live = new URLSearchParams(window.location.search);
                setSearchParams(nextInit(live), navigateOpts);
            } else {
                setSearchParams(nextInit, navigateOpts);
            }
        },
        [setSearchParams],
    );

    return [searchParams, liveSetSearchParams];
}
